import { kuunda, kuundaAnonKey, kuundaUrl, sanitizePublicError } from '@/lib/kuunda';

/** Limite upload (JSON base64) — 8 Mo fichier brut max côté fonction. */
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

type BunnyFnResult = {
  ok?: boolean;
  error?: string;
  message?: string;
  msg?: string;
  request_id?: string;
  storage_path?: string;
  url?: string;
  signedUrl?: string;
  secrets?: Record<string, boolean>;
};

function functionsUrl(): string {
  return `${(kuundaUrl ?? '').replace(/\/$/, '')}/functions/v1/bunny-storage`;
}

async function authHeaders(): Promise<Headers> {
  await kuunda.auth.initialize();
  const token = kuunda.auth.getSession()?.access_token;
  if (!token) throw new Error('Session expirée — reconnectez-vous');
  const headers = new Headers({
    Authorization: `Bearer ${token}`,
    apikey: kuundaAnonKey ?? '',
    'Content-Type': 'application/json',
  });
  // Ne pas envoyer Accept-Profile / Content-Profile vers /functions/v1 (403 gateway).
  return headers;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
    reader.readAsDataURL(file);
  });
}

async function parseJson(res: Response): Promise<BunnyFnResult> {
  return ((await res.json().catch(() => ({}))) ?? {}) as BunnyFnResult;
}

function errorFromResponse(res: Response, payload: BunnyFnResult, fallback: string): Error {
  const raw = payload.error ?? payload.message ?? payload.msg;
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (res.status === 401 || /non authentifié|session invalide|session expir/i.test(text)) {
    return new Error('Session expirée — reconnectez-vous');
  }
  return new Error(sanitizePublicError(text, fallback));
}

async function callBunny(body: Record<string, unknown>): Promise<BunnyFnResult> {
  const res = await fetch(functionsUrl(), {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  const payload = await parseJson(res);
  if (!res.ok || payload.error) {
    throw errorFromResponse(res, payload, 'Envoi du fichier impossible. Réessayez dans un instant.');
  }
  return payload;
}

async function uploadViaFunction(opts: {
  file: File;
  storagePath: string;
  kind: 'registration' | 'resource' | 'marketplace';
  registrationId?: string;
}): Promise<{ storage_path: string; url?: string }> {
  if (opts.file.size > MAX_UPLOAD_BYTES) {
    throw new Error('Fichier trop volumineux (max 8 Mo)');
  }
  const file_base64 = await fileToBase64(opts.file);
  const result = await callBunny({
    action: 'upload',
    kind: opts.kind,
    storage_path: opts.storagePath,
    registration_id: opts.registrationId,
    content_type: opts.file.type || 'application/octet-stream',
    file_base64,
  });
  return {
    storage_path: String(result.storage_path || opts.storagePath),
    url: typeof result.url === 'string' ? result.url : undefined,
  };
}

export async function uploadContestDocument(opts: {
  registrationId: string;
  storagePath: string;
  file: File;
}): Promise<void> {
  await uploadViaFunction({
    file: opts.file,
    storagePath: opts.storagePath,
    kind: 'registration',
    registrationId: opts.registrationId,
  });
}

export async function deleteContestDocument(opts: {
  registrationId: string;
  storagePath: string;
}): Promise<void> {
  await callBunny({
    action: 'delete',
    registration_id: opts.registrationId,
    storage_path: opts.storagePath,
  });
}

export async function signContestDocumentUrl(opts: {
  registrationId: string;
  storagePath: string;
  ttlSec?: number;
}): Promise<string> {
  const result = await callBunny({
    action: 'sign',
    registration_id: opts.registrationId,
    storage_path: opts.storagePath,
    ttl_sec: opts.ttlSec ?? 3600,
  });
  if (typeof result.signedUrl !== 'string') throw new Error('URL signée inaccessible');
  return result.signedUrl;
}

export async function uploadContestResourceDocument(opts: {
  storagePath: string;
  file: File;
}): Promise<void> {
  await uploadViaFunction({
    file: opts.file,
    storagePath: opts.storagePath,
    kind: 'resource',
  });
}

export async function deleteContestResourceDocument(opts: { storagePath: string }): Promise<void> {
  await callBunny({
    action: 'delete',
    kind: 'resource',
    storage_path: opts.storagePath,
  });
}

export async function signContestResourceDocumentUrl(opts: {
  storagePath: string;
  ttlSec?: number;
}): Promise<string> {
  const result = await callBunny({
    action: 'sign',
    kind: 'resource',
    storage_path: opts.storagePath,
    ttl_sec: opts.ttlSec ?? 3600,
  });
  if (typeof result.signedUrl !== 'string') throw new Error('URL signée inaccessible');
  return result.signedUrl;
}

export async function uploadMarketplaceImage(opts: {
  userId: string;
  file: File;
}): Promise<{ storage_path: string; url: string }> {
  const safeName = opts.file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `marketplace/${opts.userId}/${Date.now()}-${safeName}`;
  const result = await uploadViaFunction({
    file: opts.file,
    storagePath,
    kind: 'marketplace',
  });
  if (!result.url) throw new Error('Upload image impossible (URL manquante)');
  return { storage_path: result.storage_path, url: result.url };
}

/** Upload générique staff sous resources/ ou network/ (Bunny kind=resource). */
export async function uploadStaffResourceFile(opts: {
  storagePath: string;
  file: File;
}): Promise<void> {
  return uploadContestResourceDocument(opts);
}

export async function deleteStaffResourceFile(opts: { storagePath: string }): Promise<void> {
  return deleteContestResourceDocument(opts);
}

export async function signStaffResourceUrl(opts: {
  storagePath: string;
  ttlSec?: number;
}): Promise<string> {
  return signContestResourceDocumentUrl(opts);
}
